import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as songsApi from "../../api/songsApi";
import * as catalogApi from "../../api/catalogApi";
import { addSongToLibrary } from "../../actions/libraryActions";
import { showInfo } from "../../actions/noticeActions";
import SectionLines from "../common/SectionLines";
import { sortedVerses, verseLabel } from "../../lib/lyrics";

const problemMessage = (error) => {
  const data = error.response?.data;
  if (data?.title) return data.detail ? `${data.title} ${data.detail}` : data.title;
  if (typeof data === "string" && data) return data;
  if (error.code === "ECONNABORTED") return "The import took too long. Try again, or paste the lyrics into the editor.";
  return "Could not import the song, please try again.";
};

const hostOf = (url) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

/** The draft as the editor and the save call expect it. */
const toForm = (draft) => ({
  title: draft.Title,
  artist: draft.Artist || "",
  key: draft.Key || "",
  capo: draft.Capo ? String(draft.Capo) : "",
  lyrics: draft.Lyrics,
  public: false,
});

/**
 * Imports a song from a link: the server fetches the page, an AI model rewrites the song in
 * the app's format, and the host reviews it here before it is stored. Hosts only. Inside
 * the host's sheet (`inSheet`) the song is handed back instead of going to the favourites.
 */
const SongImportPage = ({ inSheet = false, onBack, onAdded, onEdit }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("idle"); // idle | fetching | preview | saving
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);

  // Warn when a song with the draft's title is already in the lists; adding stays possible.
  const title = draft?.Title?.trim() || "";
  useEffect(() => {
    if (!title) return undefined;
    let cancelled = false;
    catalogApi
      .search(title)
      .then((matches) => {
        if (cancelled) return;
        setDuplicate(matches.find((song) => song.Title.trim().toLowerCase() === title.toLowerCase()) || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [title]);

  const fetchSong = async (event) => {
    event.preventDefault();
    const link = url.trim();
    if (!link || status === "fetching") return;
    setStatus("fetching");
    setError(null);
    setDraft(null);
    setDuplicate(null);
    try {
      setDraft(await songsApi.importFromUrl(link));
      setStatus("preview");
    } catch (err) {
      setError(problemMessage(err));
      setStatus("idle");
    }
  };

  const accept = async () => {
    setStatus("saving");
    setError(null);
    try {
      const created = await songsApi.addGuitarSong(toForm(draft));
      dispatch(addSongToLibrary(created.Id));
      if (inSheet) {
        onAdded?.(created);
        return;
      }
      dispatch(showInfo("Added to favorites", { sub: `${created.Title} · saved offline` }));
      navigate("/library");
    } catch (err) {
      setError(problemMessage(err));
      setStatus("preview");
    }
  };

  const editFirst = () => (inSheet ? onEdit?.(toForm(draft)) : navigate("/songs/new", { state: { draft: toForm(draft) } }));
  const back = () => (inSheet ? onBack?.() : navigate(-1));

  const busy = status === "fetching" || status === "saving";
  const verses = draft ? sortedVerses(draft.Song) : [];

  return (
    <div className={inSheet ? "stack" : "page"} style={inSheet ? { flex: 1, minHeight: 0, gap: 10 } : undefined} data-screen-label={inSheet ? "Host sheet · import" : "Import from a link"}>
      {inSheet ? (
        <div className="sheet-head" style={{ marginBottom: 2 }}>
          <button type="button" className="btn btn-round btn-xs btn-outline" onClick={back} aria-label="Back">
            ←
          </button>
          <span className="sheet-title">Import from a link</span>
        </div>
      ) : (
        <>
          <button type="button" className="back-link" onClick={back} disabled={busy}>
            ← Back
          </button>
          <h2 className="page-title" style={{ fontSize: 26 }}>
            Import from a link
          </h2>
        </>
      )}
      <form className="editor-meta" onSubmit={fetchSong}>
        <input id="import-url" className="field" type="url" inputMode="url" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://…" aria-label="Link to a page with the song" autoComplete="off" spellCheck={false} disabled={busy} />
        <button type="submit" className="btn btn-primary" style={{ flex: "none" }} disabled={busy || url.trim().length === 0}>
          {status === "fetching" ? "Reading…" : "Fetch song"}
        </button>
      </form>
      {status === "idle" && !draft && (
        <p className="note">The page is read once and the song on it is rewritten in the app’s format by an AI model. Check the result before adding it; nothing is stored until you do.</p>
      )}
      {status === "fetching" && (
        <p className="note" role="status" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="dot" style={{ background: "var(--accent)" }} aria-hidden="true" />
          Reading the page and extracting the lyrics… this can take up to a minute.
        </p>
      )}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {draft && status !== "fetching" && (
        <section className="stack" style={{ flex: 1, minHeight: 0 }} aria-label="Imported song">
          <div className={inSheet ? "" : "card-soft"}>
            <div className="row-title" style={{ fontSize: 18 }}>
              {draft.Title}
            </div>
            <div className="row-sub" style={{ color: inSheet ? "var(--sheet-muted)" : "var(--accent-muted)" }}>
              {[draft.Artist, draft.Key ? `Key ${draft.Key}` : null].filter(Boolean).join(" · ")}
              {" · from "}
              <a href={draft.SourceUrl} target="_blank" rel="noreferrer">
                {hostOf(draft.SourceUrl)}
              </a>
            </div>
          </div>
          {draft.Warnings?.length > 0 && (
            <ul className="note">
              {draft.Warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
          {duplicate && (
            <p className="warn-text" role="status">
              A song called “{duplicate.Title}” already exists.{" "}
              {!inSheet && (
                <button type="button" className="link-btn" onClick={() => navigate(`/songs/${duplicate.Id}/edit`)} disabled={busy}>
                  Edit that one instead
                </button>
              )}
            </p>
          )}
          <div className="section-list" style={inSheet ? { flex: 1, minHeight: 0, overflowY: "auto", gap: 14 } : undefined}>
            {verses.map((verse, index) => (
              <div key={verse.Order} className="section">
                <span className={`kicker${inSheet ? " kicker-accent" : ""}`}>{verseLabel(verse, index)}</span>
                <SectionLines verse={verse} showChords size="xs" />
              </div>
            ))}
          </div>
          <div className="btn-row" style={{ flex: "none" }}>
            <button type="button" className="btn btn-md btn-outline" onClick={editFirst} disabled={busy}>
              Edit first
            </button>
            <button type="button" className="btn btn-md btn-primary" onClick={accept} disabled={busy}>
              {status === "saving" ? "Adding…" : inSheet ? "Add next" : "Add to favorites"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default SongImportPage;
